// TODO: Fix potential circular dependency issues
import type { Study } from './current-study-store';

import { orthancJwt } from '@src/core/dicom-web-api';
import {
  AnalysisType,
  Orientation,
} from '@src/store/dicom-web/classification-typing';

// import { useSegmentationSettingsStore } from '@src/store/contours/contour-segmentation';
import { useOrthancStore } from '@src/store/dicom-web/orthanc-store';
import z from 'zod';

import { doubleKeySchema } from '@/src/utils/doubleKeyRecord';
import { getMessage } from '@/src/utils/exception';

import { useViewportStateStore } from '../contours/viewport-state-store';
import { useCurrentStudyStore } from './current-study-store';

const { VITE_OCG_BACKEND_SEGMENT_URL } = import.meta.env;

export interface SeriesClassificationResult {
  orientation: Orientation;
  analysisType: AnalysisType;
  cine: boolean;
  parallelStack: boolean;
  numberOfImagesSeries: number;
  confidence?: number; // Optional confidence score for the classification
  mode?: string; // Auto or Manual
}

export const ClassificationSchema = z.object({
  sequence: z.string(),
  orientation: z.string(),
  probability: z.string(),
});
export type Classification = z.infer<typeof ClassificationSchema>;

// [seriesUID][normalKey]
export const BackendClassificationResultSchema = z.record(z.string(), z.record(z.string(), ClassificationSchema));
export type BackendClassificationResult = z.infer<typeof BackendClassificationResultSchema>;

/**
 * Extended classification result that includes all backend and local tags
 * Used for serialization/deserialization
 */
export const SerializedClassificationResultSchema = ClassificationSchema.extend({
  analysisType: z.string().optional(),
  cine: z.boolean().optional(),
  parallelStack: z.boolean().optional(),
  numberOfImagesSeries: z.number().optional(),
  confidence: z.number().optional(),
  mode: z.string().optional(),
});
export type SerializedClassificationResult = z.infer<typeof SerializedClassificationResultSchema>;

export const SerializedSeriesClassificationSchema = doubleKeySchema(SerializedClassificationResultSchema);
export type SerializedSeriesClassification = z.infer<typeof SerializedSeriesClassificationSchema>;

export class SeriesClassification {
  private classificationsMap: Record<
    string,
    Record<string, SeriesClassificationResult>
  > = {};

  private initialized: boolean = false;

  constructor() {
    this.initialized = false;
    this.classificationsMap = {};
  }

  init() {
    this.reset();
    const study = useCurrentStudyStore().activeStudy;
    if (!study) {
      console.warn('No study loaded, classification cannot proceed');
      return;
    }

    this.classifySeries(study);
  }

  reset(): void {
    this.classificationsMap = {};
    this.initialized = false;
  }

  private checkShortAxis(normal: number[]) {
    if (
      (normal[0] > -0.98
        && normal[0] < -0.4
        && normal[1] > 0.25
        && normal[1] < 0.85
        && normal[2] >= 0.0
        && normal[2] < 0.8)
      || (normal[0] > 0.4
        && normal[0] < 0.98
        && normal[1] > -0.85
        && normal[1] < -0.25
        && normal[2] > -0.8
        && normal[2] <= 0.0)
    ) {
      return true;
    }
    return false;
  }

  /**
   * Classify series using both local heuristics and backend classification results
   * @param study - The study to classify
   */
  private classifySeries(study: Study) {
    const { series } = study;
    if (!series || series.length === 0) {
      console.warn(
        'SeriesClassification - No series found in the current study',
      );
      return;
    }

    for (const seriesItem of series) {
      const { slices } = seriesItem;
      if (!slices || slices.length === 0) {
        console.warn(
          'SeriesClassification - No slices found in the current series',
          seriesItem.uid,
        );
        continue;
      }

      // Initialize the series in the map
      if (!this.classificationsMap[seriesItem.uid]) {
        this.classificationsMap[seriesItem.uid] = {};
      }

      // Process each slice
      for (const slice of slices) {
        if (!slice.images || slice.images.length === 0) {
          console.warn(
            'SeriesClassification - No images found in slice of series',
            seriesItem.uid,
          );
          continue;
        }

        const firstImage = slice.images[0];
        const firstImageUID = firstImage.uid;
        const normKey
          = useCurrentStudyStore().getSliceKeyFromUID(firstImageUID);

        if (!normKey) {
          console.warn(
            'SeriesClassification - Could not determine norm key for image',
            firstImageUID,
          );
          continue;
        }

        const seriesDescription
          = useCurrentStudyStore().seriesInfoDicom[seriesItem.uid]?.SeriesDescription || '';

        const numberOfSlices = slices.length;
        const numberOfImagesSlice = slice.images.length;

        let numberOfImagesSeries = 0;

        for (const s of slices) {
          if (s.images && s.images.length > 0) {
            numberOfImagesSeries += s.images.length;
          }
        }

        const firstImageInfo
          = useCurrentStudyStore().imageInfoDicom[firstImageUID];

        const isSax = this.checkShortAxis(firstImageInfo.ImageNormalVector);
        const descriptionBold
          = seriesDescription?.includes('BOLD')
            || seriesDescription?.includes('CINE OS')
            || seriesDescription?.includes('B MORE');

        // Start with local heuristic classification
        const localClassification: SeriesClassificationResult = {
          orientation: isSax ? Orientation.Sax : Orientation.Undefined,
          analysisType:
            descriptionBold && isSax && numberOfSlices === 2
              ? AnalysisType.OCG
              : AnalysisType.Function,
          cine: numberOfImagesSlice > 15,
          parallelStack: isSax && !descriptionBold,
          numberOfImagesSeries,
        };

        localClassification.mode = 'Manual';

        this.classificationsMap[seriesItem.uid][normKey] = localClassification;
      }
    }

    this.initialized = true;
  }

  /**
   * Apply default viewport mappings based on classifications
   * Maps series to viewports with appropriate slice indices
   */
  public applyDefaultViewportMapping(): void {
    // Find the BOLD series with the largest image count
    const boldSeriesUID = this.findBoldSeriesWithMaxImages();

    // Find the first SAX parallel stack series
    const saxParallelStackUID = this.findSaxParallelStackSeries();

    // Apply mappings
    this.mapSeriestoViewport('FunctionMainView', saxParallelStackUID, 0);
    this.mapBoldSeriesToContours(
      'ContoursLeft',
      'ContoursRight',
      boldSeriesUID,
    );
    this.mapFirstFourSeriesToViewer();
  }

  private buildSeriesIndicesForViewers(
    availableSeriesCount: number,
    viewportCount: number,
  ): number[] {
    const indices: number[] = [];

    for (let i = 0; i < viewportCount; i++) {
      indices.push(i % availableSeriesCount);
    }

    return indices;
  }

  /**
   * Find the BOLD series UID with the maximum image count
   */
  private findBoldSeriesWithMaxImages(): string | null {
    let maxImageCount = 0;
    let boldSeriesUID: string | null = null;

    for (const [seriesUID, seriesClassifications] of Object.entries(
      this.classificationsMap,
    )) {
      for (const classification of Object.values(seriesClassifications)) {
        if (
          classification.analysisType === AnalysisType.OCG
          && classification.numberOfImagesSeries > maxImageCount
        ) {
          maxImageCount = classification.numberOfImagesSeries;
          boldSeriesUID = seriesUID;
        }
      }
    }

    return boldSeriesUID;
  }

  /**
   * Find the first SAX parallel stack series UID
   */
  private findSaxParallelStackSeries(): string | null {
    for (const [seriesUID, seriesClassifications] of Object.entries(
      this.classificationsMap,
    )) {
      for (const classification of Object.values(seriesClassifications)) {
        if (
          classification.parallelStack
          && classification.orientation === Orientation.Sax
        ) {
          return seriesUID;
        }
      }
    }
    return null;
  }

  /**
   * Map a series to a single viewport
   */
  private mapSeriestoViewport(
    viewportId: string,
    seriesUID: string | null,
    sliceIdx: number = 0,
  ): void {
    if (!seriesUID) return;

    const seriesIndex = this.getSeriesIndex(seriesUID);
    if (seriesIndex === -1) return;

    useViewportStateStore().setSelectedImageIndices(
      viewportId,
      {
        seriesIdx: seriesIndex,
        sliceIdx,
        imageIdx: 0,
      },
      true,
    );
  }

  /**
   * Map a BOLD series to left and right contour viewports
   */
  private mapBoldSeriesToContours(
    leftViewportId: string,
    rightViewportId: string,
    seriesUID: string | null,
  ): void {
    if (!seriesUID) return;

    const seriesIndex = this.getSeriesIndex(seriesUID);
    if (seriesIndex === -1) return;

    const viewportStore = useViewportStateStore();

    viewportStore.setSelectedImageIndices(
      leftViewportId,
      {
        seriesIdx: seriesIndex,
        sliceIdx: 0,
        imageIdx: 0,
      },
      true,
    );

    viewportStore.setSelectedImageIndices(
      rightViewportId,
      {
        seriesIdx: seriesIndex,
        sliceIdx: 1,
        imageIdx: 0,
      },
      true,
    );
  }

  /**
   * Map the first four series to the viewer grid
   */
  private mapFirstFourSeriesToViewer(): void {
    const viewerMappings = [
      'ViewerComponentTopLeft',
      'ViewerComponentTopRight',
      'ViewerComponentBottomLeft',
      'ViewerComponentBottomRight',
    ];

    const study = useCurrentStudyStore().activeStudy;
    if (!study?.series || study.series.length === 0) return;

    // Build series indices, repeating if necessary
    const seriesIndices = this.buildSeriesIndicesForViewers(
      study.series.length,
      viewerMappings.length,
    );

    const viewportStore = useViewportStateStore();

    for (let i = 0; i < viewerMappings.length; i++) {
      viewportStore.setSelectedImageIndices(
        viewerMappings[i],
        {
          seriesIdx: seriesIndices[i],
          sliceIdx: 0,
          imageIdx: 0,
        },
        true,
      );
    }
  }

  /**
   * Get the series index from a series UID
   */

  private getSeriesIndex(seriesUID: string): number {
    const study = useCurrentStudyStore().activeStudy;
    if (!study?.series) return -1;

    return study.series.findIndex(series => series.uid === seriesUID);
  }

  public async triggerAutoClassification() {
    const currentStudyStore = useCurrentStudyStore();
    const orthancStore = useOrthancStore();
    const orthancConfig = await orthancStore.getConfig();
    const studyUID = currentStudyStore.getStudyID;
    const authToken = (await orthancJwt(orthancConfig.id)) || 'N/A';

    const orthancAuthConfig = {
      url: orthancConfig.url,
      auth_token: authToken,
    };

    const payload = {
      StudyInstanceUID: studyUID,
      ORTHANC_CONFIG_ID: orthancConfig.id,
    };

    const response = await fetch(
      `${VITE_OCG_BACKEND_SEGMENT_URL}classify_images/v2`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-control': 'no-cache',
          'Connection': 'keep-alive',
        },
        body: JSON.stringify({
          dicomPayload: payload,
          orthancAuthConfig,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const ResponseSchema = z.object({
      classification_result: BackendClassificationResultSchema,
    });

    try {
      const classificationResults = ResponseSchema.parse(await response.json()).classification_result;
      this.adjustClassification(classificationResults);

      return classificationResults;
    }
    catch (error) {
      console.error('Could not parse classification result (%s)', getMessage(error));
      return undefined;
    }
  }

  // Getters
  isInitialized(): boolean {
    return this.initialized;
  }

  getClassificationBySopInstanceUID(
    sopInstanceUID: string,
  ): SeriesClassificationResult | undefined {
    const seriesUID
      = useCurrentStudyStore().getSeriesUIDByImageUID(sopInstanceUID);
    if (!seriesUID) {
      console.warn(
        `No series found for image UID ${sopInstanceUID}, cannot determine classification`,
      );
      return undefined;
    }

    const normalKey = useCurrentStudyStore().getSliceKeyFromUID(sopInstanceUID);
    if (!normalKey) {
      console.warn(
        `No normal key found for image UID ${sopInstanceUID}, cannot determine classification`,
      );
      return undefined;
    }

    // First check if we have a classification for this series and slice
    if (
      !this.classificationsMap[seriesUID]
      || !this.classificationsMap[seriesUID][normalKey]
    ) {
      return undefined;
    }

    const classification = this.classificationsMap[seriesUID][normalKey];
    return classification;
  }

  getClassificationByViewport(
    viewport: string,
  ): SeriesClassificationResult | undefined {
    const sopImageUIDS
      = useCurrentStudyStore().getSliceImageUIDsbyView(viewport);
    const firstImageUID = sopImageUIDS?.[0];
    if (!firstImageUID) {
      console.warn(
        `No image found for viewport ${viewport}, cannot determine classification`,
      );
      return undefined;
    }
    const seriesUID
      = useCurrentStudyStore().getSeriesUIDByImageUID(firstImageUID);

    if (!seriesUID) {
      console.warn(
        `No series found for image UID ${firstImageUID}, cannot determine classification`,
      );
      return undefined;
    }

    const normalKey = useCurrentStudyStore().getSliceKeyFromUID(firstImageUID);
    if (!normalKey) {
      console.warn(
        `No normal key found for image UID ${firstImageUID}, cannot determine classification`,
      );
      return undefined;
    }
    // First check if we have a classification for this series and slice
    if (
      !this.classificationsMap[seriesUID]
      || !this.classificationsMap[seriesUID][normalKey]
    ) {
      return undefined;
    }

    const classification = this.classificationsMap[seriesUID][normalKey];
    return classification;
  }

  /**
   * Serialize classifications to a format compatible with BackendClassificationResult
   * but extended with all local classification fields
   * @returns Serialized classification data
   */
  serialize(): SerializedSeriesClassification {
    const serialized = {} as SerializedSeriesClassification;

    for (const [seriesUID, seriesClassifications] of Object.entries(
      this.classificationsMap,
    )) {
      serialized[seriesUID] = {};

      for (const [normKey, classification] of Object.entries(
        seriesClassifications,
      )) {
        serialized[seriesUID][normKey] = {
          // Backend-compatible fields
          sequence: classification.cine ? 'CINE' : 'STATIC',
          orientation: classification.orientation,
          probability: classification.confidence?.toString() ?? '0',
          // Extended local fields
          analysisType: classification.analysisType,
          cine: classification.cine,
          parallelStack: classification.parallelStack,
          numberOfImagesSeries: classification.numberOfImagesSeries,
          confidence: classification.confidence,
          mode: classification.mode,
        };
      }
    }

    // convert to JSON string and back to ensure it's serializable as JSON
    return serialized;
  }

  /**
   * Deserialize classifications from a serialized format (e.g., stored data)
   * Merges with existing classifications and updates them
   * @param data Serialized classification data
   */
  deserialize(
    data:
      | BackendClassificationResult
      | SerializedSeriesClassification,
  ): void {
    for (const [seriesUID, seriesClassifications] of Object.entries(data)) {
      if (!this.classificationsMap[seriesUID]) {
        this.classificationsMap[seriesUID] = {};
      }

      for (const [normKey, classificationData] of Object.entries(
        seriesClassifications,
      )) {
        const {
          sequence,
          orientation,
          probability,
          analysisType,
          cine,
          parallelStack,
          numberOfImagesSeries,
          confidence,
          mode,
        } = classificationData as SerializedClassificationResult;

        // Reconstruct the classification object
        const classification: SeriesClassificationResult = {
          orientation: (orientation as Orientation) || Orientation.Undefined,
          analysisType:
            (analysisType as AnalysisType) || AnalysisType.Undefined,
          cine: cine !== undefined ? cine : sequence === 'CINE',
          parallelStack: parallelStack ?? false,
          numberOfImagesSeries: numberOfImagesSeries ?? 0,
          confidence:
            confidence
            ?? (probability ? Number.parseFloat(probability) : undefined),
          mode: mode ?? 'Auto', // Default to 'Auto' for backend results
        };

        this.classificationsMap[seriesUID][normKey] = classification;
      }
    }
  }

  adjustClassificationByViewport(
    viewportID: string,
    updatedFields: Partial<SeriesClassificationResult>,
  ): void {
    const sopImageUIDS
      = useCurrentStudyStore().getSliceImageUIDsbyView(viewportID);
    const firstImageUID = sopImageUIDS?.[0];
    if (!firstImageUID) {
      console.warn(
        `No image found for viewport ${viewportID}, cannot adjust classification`,
      );
      return;
    }
    const seriesUID
      = useCurrentStudyStore().getSeriesUIDByImageUID(firstImageUID);

    if (!seriesUID) {
      console.warn(
        `No series found for image UID ${firstImageUID}, cannot adjust classification`,
      );
      return;
    }

    const normalKey = useCurrentStudyStore().getSliceKeyFromUID(firstImageUID);
    if (!normalKey) {
      console.warn(
        `No normal key found for image UID ${firstImageUID}, cannot adjust classification`,
      );
      return;
    }

    if (!this.classificationsMap[seriesUID]) {
      this.classificationsMap[seriesUID] = {};
    }

    if (!this.classificationsMap[seriesUID][normalKey]) {
      this.classificationsMap[seriesUID][normalKey] = {
        orientation: Orientation.Undefined,
        analysisType: AnalysisType.Undefined,
        cine: false,
        parallelStack: false,
        numberOfImagesSeries: 0,
      };
      return;
    }

    const existingClassification
      = this.classificationsMap[seriesUID][normalKey];
    this.classificationsMap[seriesUID][normalKey] = {
      ...existingClassification,
      ...updatedFields,
    };
  }

  /**
   * Adjust classifications with backend results
   * Works similarly to deserialize but specifically for backend integration
   * @param backendResult - Classification result from backend
   */
  adjustClassification(backendResult: BackendClassificationResult) {
    const study = useCurrentStudyStore().activeStudy;

    if (!study) {
      console.warn('No study loaded, classification cannot proceed');
      return;
    }

    const { series } = study;

    for (const seriesItem of series) {
      const seriesUID = seriesItem.uid;
      const { slices } = seriesItem;

      for (const slice of slices) {
        const firstImage = slice.images?.[0];
        const normKey = useCurrentStudyStore().getSliceKeyFromUID(
          firstImage?.uid ?? '',
        );

        if (!normKey) {
          continue;
        }

        const classificationForSlice = backendResult[seriesUID]?.[normKey];

        if (!classificationForSlice) {
          console.warn(
            `No classification found for series ${seriesUID} and slice ${normKey}`,
          );
          continue;
        }

        const {
          orientation: plane,
          sequence,
          probability,
        } = classificationForSlice;

        // Initialize if not exists
        if (!this.classificationsMap[seriesUID]) {
          this.classificationsMap[seriesUID] = {};
        }

        if (!this.classificationsMap[seriesUID][normKey]) {
          this.classificationsMap[seriesUID][normKey] = {
            orientation: Orientation.Undefined,
            analysisType: AnalysisType.Undefined,
            cine: false,
            parallelStack: false,
            numberOfImagesSeries: 0,
          };
        }

        const classification = this.classificationsMap[seriesUID][normKey];

        // Update with backend results
        if (Object.values(Orientation).includes(plane as Orientation)) {
          classification.orientation = plane as Orientation;
          classification.mode = 'Auto';
        }

        if (sequence === 'CINE') {
          classification.cine = true;
        }

        if (probability) {
          classification.confidence = Number.parseFloat(probability);
        }
      }
    }
  }

  /**
   * Get all classifications for storage/export
   */
  getAllClassifications(): Record<
    string,
    Record<string, SeriesClassificationResult>
  > {
    return structuredClone(this.classificationsMap);
  }
}

// Export a singleton instance for application-wide use
export const seriesClassification = new SeriesClassification();
